import { useSyncExternalStore } from "react";

// Convite para instalar o app (atalho em forma de aplicativo). O navegador dispara `beforeinstallprompt` cedo,
// às vezes antes do login: o evento é guardado aqui e usado quando a pessoa já estiver dentro do sistema.

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export const INSTALL_DISMISSED_KEY = "meta.gestao-veicular.install-dismissed-at";
export const INSTALL_REOFFER_DAYS = 14;

export type InstallMode = "native" | "ios" | "none";
interface InstallState { mode: InstallMode; installed: boolean; dismissed: boolean }

let deferred: InstallPromptEvent | null = null;
let state: InstallState = { mode: "none", installed: false, dismissed: false };
const listeners = new Set<() => void>();

function emit(next: Partial<InstallState>) {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function isIosBrowser(userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent, touchPoints = typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints): boolean {
  return /iphone|ipad|ipod/i.test(userAgent) || (/macintosh/i.test(userAgent) && touchPoints > 1);
}

/** Respeita "Agora não": não oferece de novo por alguns dias. */
export function wasRecentlyDismissed(now = Date.now(), storage: Pick<Storage, "getItem"> | null = safeStorage()): boolean {
  try {
    const at = Number(storage?.getItem(INSTALL_DISMISSED_KEY));
    return at > 0 && now - at < INSTALL_REOFFER_DAYS * 86_400_000;
  } catch { return false; }
}

function safeStorage(): Storage | null {
  try { return typeof localStorage === "undefined" ? null : localStorage; } catch { return null; }
}

if (typeof window !== "undefined") {
  const installed = isStandalone();
  state = { mode: !installed && isIosBrowser() ? "ios" : "none", installed, dismissed: wasRecentlyDismissed() };
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // adia o convite do navegador para mostrá-lo no momento certo
    deferred = event as InstallPromptEvent;
    emit({ mode: "native" });
  });
  window.addEventListener("appinstalled", () => { deferred = null; emit({ installed: true, mode: "none" }); });
}

export function dismissInstallPrompt() {
  try { safeStorage()?.setItem(INSTALL_DISMISSED_KEY, String(Date.now())); } catch { /* sem localStorage: some só nesta sessão */ }
  emit({ dismissed: true });
}

/** Abre a janela nativa de instalação. Retorna se a pessoa aceitou. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  await event.prompt();
  const choice = await event.userChoice.catch(() => ({ outcome: "dismissed" as const }));
  if (choice.outcome === "accepted") emit({ installed: true, mode: "none" });
  else { emit({ mode: "none" }); dismissInstallPrompt(); }
  return choice.outcome === "accepted";
}

export function useInstallState(): InstallState {
  return useSyncExternalStore((listener) => { listeners.add(listener); return () => { listeners.delete(listener); }; }, () => state, () => state);
}
